import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { register } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { useAppConfig } from "../context/AppConfigContext";
import { useTranslation } from "../i18n/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function RegisterPage() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { loginUser } = useAuth();
  const { appName, branding, loginHeader, loginLogo } = useAppConfig();
  const { t, i18nEnabled } = useTranslation();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await register({
        email,
        password,
        first_name: firstName,
        last_name: lastName,
      });
      loginUser(res.data.token, res.data.user);
      const pendingCard = sessionStorage.getItem("pending_card_code");
      if (pendingCard) {
        navigate(`/c/${pendingCard}`, { replace: true });
      } else {
        navigate("/app");
      }
    } catch (err) {
      setError(
        err.response?.data?.error || t("register.failed")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="register-page" className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div id="register-card" className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8">
        {i18nEnabled && (
          <div className="flex justify-end mb-2">
            <LanguageSwitcher />
          </div>
        )}
        {loginHeader === "logo" ? (
          <div className="flex justify-center mb-2">
            <img
              src={loginLogo || branding.logo}
              alt={appName}
              className="h-36 object-contain"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          </div>
        ) : (
          <h1 id="register-title" className="text-3xl font-bold text-center text-gray-900 mb-2">
            {appName}
          </h1>
        )}
        <p className="text-center text-gray-500 mb-8">
          {t("register.title")}
        </p>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}

        <form id="register-form" onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="firstName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("register.firstName")}
              </label>
              <input
                id="firstName"
                type="text"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label
                htmlFor="lastName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("register.lastName")}
              </label>
              <input
                id="lastName"
                type="text"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t("register.email")}
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t("register.password")}
            </label>
            <input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? t("register.submitting") : t("register.submit")}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-500">
          {t("register.hasAccount")}{" "}
          <Link
            to="/login"
            className="text-blue-600 font-medium hover:text-blue-500"
          >
            {t("register.signIn")}
          </Link>
        </p>
      </div>
    </div>
  );
}
